import {
  aggregateSessionWindowStats,
  calculateBybIqLongTermResults,
  calculateHeadlineBybIq,
  calculateMainMetricsLongTermResults,
  filterLongTermMetricResultsByDateRange,
  filterLongTermResultsByDateRange,
  getFromDate,
  parseRadarStats,
  statsConfig,
} from "@Containers/shared/progress/utils";
import { Actions, AppContext, Events } from "@Context/common/general/context";
import { DateRange, GameModes, PeriodType, ScoreFieldTypes, ScoreTypes, TimePeriods } from "@Enums";
import type { LongTermMetricResults, LongTermResultPoint, RadarMetricInfo } from "@Helpers/types";
import useDemoAccount from "@hooks/common/user/use-demo-account";
import useI18n from "@i18n";
import type { UserStats } from "@Shared/schemas/stats/statsSchemas";
import dayjs from "dayjs";
import { isNumber } from "lodash-es";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

export const MIN_BYB_IQ_FOR_PERFORMANCE_PHASE = 500;

export interface UseCommonProgressDataReturn {
  totalSessions: number;
  requiredTrainingTime: number;
  weeklyTrainingTime: number;
  totalTrainingTime: number;
  monthlyTrainingTime: number;
  weeklyDailyStats: any[];
  monthlyDailyStats: any[];
  currentStats: any;
  previousStats: any;
  radarData: RadarMetricInfo[];
  historicalResults: UserStats[];
  longTermResults: LongTermResultPoint[];
  longTermMetricResults: LongTermMetricResults | null;
  currentBybIq: number;
  improvementPercentage: number;
  isCurrentBybIqLoading: boolean;
  trainingStreakWeeks: number;
  isRecentRadarLoading: boolean;
  isPreviousRadarLoading: boolean;
  isWeekTimeLoading: boolean;
  isMonthTimeLoading: boolean;
  isTotalTimeLoading: boolean;
  isTotalSessionsLoading: boolean;
  isLongTermResultsLoading: boolean;
  setIsLongTermResultsLoading: (loading: boolean) => void;
  isWeeklyDailyStatsLoading: boolean;
  isMonthlyDailyStatsLoading: boolean;
  isStreakLoading: boolean;
  dateRange: DateRange;
  optionsAnchorElement: HTMLButtonElement | null;
  setDateRange: (dateRange: DateRange) => void;
  setOptionsAnchorElement: (element: HTMLButtonElement | null) => void;
  isInBootcamp: boolean;
  isDemo: boolean;
  navigateToChallenges: () => void;
}

export const useCommonProgressData = (): UseCommonProgressDataReturn => {
  const { state, dispatch } = useContext(AppContext);
  const { isDemo } = useDemoAccount();
  const { i18n } = useI18n();

  const [totalSessions, setTotalSessions] = useState<number>(0);
  const [requiredTrainingTime, setRequiredTrainingTime] = useState<number>(30);
  const [weeklyTrainingTime, setWeeklyTrainingTime] = useState<number>(0);
  const [totalTrainingTime, setTotalTrainingTime] = useState<number>(0);
  const [monthlyTrainingTime, setMonthlyTrainingTime] = useState<number>(0);
  const [weeklyDailyStats, setWeeklyDailyStats] = useState<any[]>([]);
  const [monthlyDailyStats, setMonthlyDailyStats] = useState<any[]>([]);
  const [trainingStreakWeeks, setTrainingStreakWeeks] = useState<number>(0);

  const [currentStats, setCurrentStats] = useState<any>(null);
  const [previousStats, setPreviousStats] = useState<any>(null);
  const [radarData, setRadarData] = useState<RadarMetricInfo[]>([]);
  const [currentBybIq, setCurrentBybIq] = useState<number>(-1);
  const [isCurrentBybIqLoading, setIsCurrentBybIqLoading] = useState<boolean>(true);

  const [historicalResults, setHistoricalResults] = useState<UserStats[]>([]);
  const [longTermResults, setLongTermResults] = useState<LongTermResultPoint[]>([]);
  const [longTermMetricResults, setLongTermMetricResults] = useState<LongTermMetricResults | null>(null);

  const [isRecentRadarLoading, setIsRecentRadarLoading] = useState<boolean>(true);
  const [isPreviousRadarLoading, setIsPreviousRadarLoading] = useState<boolean>(true);
  const [isWeekTimeLoading, setIsWeekTimeLoading] = useState<boolean>(true);
  const [isMonthTimeLoading, setIsMonthTimeLoading] = useState<boolean>(true);
  const [isTotalTimeLoading, setIsTotalTimeLoading] = useState<boolean>(true);
  const [isTotalSessionsLoading, setIsTotalSessionsLoading] = useState<boolean>(true);
  const [isLongTermResultsLoading, setIsLongTermResultsLoading] = useState<boolean>(true);
  const [isWeeklyDailyStatsLoading, setIsWeeklyDailyStatsLoading] = useState<boolean>(true);
  const [isMonthlyDailyStatsLoading, setIsMonthlyDailyStatsLoading] = useState<boolean>(true);
  const [isStreakLoading, setIsStreakLoading] = useState<boolean>(true);

  const [dateRange, setDateRange] = useState<DateRange>(DateRange.PAST_14_DAYS);
  const [optionsAnchorElement, setOptionsAnchorElement] = useState<HTMLButtonElement | null>(null);

  const isInBootcamp = isNumber(state.user.userRating) && state.user.userRating < 2000 && !isDemo;

  useEffect(() => {
    const creationDate = state.user.creationDate;
    const creationTimestamp = creationDate instanceof Date ? creationDate.getTime() / 1000 : typeof creationDate === "number" ? creationDate : null;
    const isLessThan3MonthsOld = creationTimestamp ? creationTimestamp < dayjs().subtract(3, "month").unix() : false;
    const isLessThan8DaysOld = creationTimestamp ? creationTimestamp < dayjs().subtract(8, "day").unix() : false;

    let defaultDateRange = DateRange.PAST_YEAR;
    if (isLessThan3MonthsOld) defaultDateRange = DateRange.PAST_MONTH;
    if (isLessThan8DaysOld) defaultDateRange = DateRange.PAST_14_DAYS;

    setDateRange(defaultDateRange);
  }, []);

  const calculateTrainingStreak = (sessions: any[]): number => {
    if (!sessions || sessions.length === 0) return 0;

    const weekKeys = sessions
      .filter(session => session.updated_at)
      .map(session => dayjs(session.updated_at).startOf("week").format("YYYY-MM-DD"));

    const uniqueWeeksDesc = Array.from(new Set(weekKeys)).sort((a, b) => dayjs(b).diff(dayjs(a)));
    if (uniqueWeeksDesc.length === 0) return 0;

    const currentWeekStart = dayjs().startOf("week").format("YYYY-MM-DD");
    const lastWeekStart = dayjs().subtract(1, "week").startOf("week").format("YYYY-MM-DD");

    const mostRecentWeek = uniqueWeeksDesc[0];
    if (mostRecentWeek !== currentWeekStart && mostRecentWeek !== lastWeekStart) return 0;

    let streakWeeks = 0;
    let expectedWeek = mostRecentWeek;

    for (const week of uniqueWeeksDesc) {
      if (week === expectedWeek) {
        streakWeeks += 1;
        expectedWeek = dayjs(expectedWeek).subtract(1, "week").startOf("week").format("YYYY-MM-DD");
      } else {
        break;
      }
    }

    return streakWeeks;
  };

  const navigateToChallenges = () => {
    Events.app.navigate.emit({ path: `/challenges/` });
  };

  const getHistoricalMetricsAndResults = useCallback((): Promise<void> => {
    setIsLongTermResultsLoading(true);
    const currentYear = dayjs().year();
    const startYear = 2018;
    const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i);
    const allResults: UserStats[] = [];

    const yearPromises = years.map(
      year =>
        new Promise<void>(resolve => {
          dispatch({
            type: Actions.StatsActions.GetUserStats,
            payload: {
              user_ids: [state.user.id],
              from_date: dayjs(`${year}-01-01`).startOf("day").toDate(),
              to_date: dayjs(`${year}-12-31`).endOf("day").toDate(),
              group_by_period: TimePeriods.DATE,
              game_mode: GameModes.SCENARIO,
              is_completed: true,
              stats: statsConfig,
            },
            disableLoaders: true,
            cacheTime: [5, "minutes"],
            onSuccess: (stats: UserStats[]) => {
              if (stats && Array.isArray(stats)) {
                const statsWithYear = stats.map((stat: UserStats) => ({ ...stat, year }));
                allResults.push(...statsWithYear);
              }
              resolve();
            },
            onError: () => {
              resolve();
            },
          });
        })
    );

    return Promise.all(yearPromises).then(() => {
      setHistoricalResults(allResults);
      if (allResults.length === 0) {
        setLongTermResults([]);
        setLongTermMetricResults(null);
        setIsLongTermResultsLoading(false);
      }
    });
  }, [state.user.id, dispatch]);

  useEffect(() => {
    getHistoricalMetricsAndResults();
  }, [getHistoricalMetricsAndResults]);

  useEffect(() => {
    if (!dateRange || !historicalResults?.length) return;

    const bybIqResults = calculateBybIqLongTermResults(historicalResults, TimePeriods.DATE, i18n, dateRange);
    const metricResults = calculateMainMetricsLongTermResults(historicalResults, TimePeriods.DATE, i18n, dateRange);

    setLongTermResults(filterLongTermResultsByDateRange(bybIqResults, dateRange));
    setLongTermMetricResults(filterLongTermMetricResultsByDateRange(metricResults, dateRange));
    setIsLongTermResultsLoading(false);
  }, [dateRange, historicalResults, i18n]);

  const improvementPercentage = useMemo(() => {
    if (isCurrentBybIqLoading || currentBybIq < 0) {
      return 0;
    }

    const bybIqValue = currentBybIq ?? 0;
    const nonNullResults = longTermResults.filter(result => result.score !== null && result.score !== undefined);

    if (nonNullResults.length === 0) {
      return 0;
    }

    const avgFromPeriod = nonNullResults.reduce((acc, result) => acc + (result.score || 0), 0) / nonNullResults.length;

    if (avgFromPeriod === 0) {
      return 0;
    }

    const diff = ((bybIqValue - avgFromPeriod) / avgFromPeriod) * 100;
    return Math.round(diff);
  }, [currentBybIq, isCurrentBybIqLoading, longTermResults]);

  useEffect(() => {
    const apiPromises: Array<Promise<void>> = [];

    // Get User Stats last 30 sessions and previous 30 sessions
    [
      {
        limit: 30,
        offset: 0,
        setStats: setCurrentStats,
        setLoading: setIsRecentRadarLoading,
      },
      {
        limit: 30,
        offset: 30,
        setStats: setPreviousStats,
        setLoading: setIsPreviousRadarLoading,
      },
    ].forEach(config => {
      const payload: any = {
        user_ids: [state.user.id],
        stats: statsConfig,
        offset: config.offset,
        limit: config.limit,
        game_mode: GameModes.SCENARIO,
        is_completed: true,
        order_by: {
          field: "ended_at",
          direction: "DESC",
        },
      };

      apiPromises.push(
        dispatch({
          type: Actions.StatsActions.GetUserStats,
          payload: payload,
          disableLoaders: true,
          cacheTime: [5, "minutes"],
          onSuccess: (stats: UserStats[]) => {
            config.setLoading(false);
            config.setStats(aggregateSessionWindowStats(stats));
          },
        })
      );
    });

    // Get total sessions recorded
    apiPromises.push(
      dispatch({
        type: Actions.StatsActions.GetUserSessions,
        payload: {
          user_id: state.user.id,
          is_completed: true,
          game_mode: GameModes.SCENARIO
        },
        disableLoaders: true,
        cacheTime: [5, "minutes"],
        onSuccess: data => {
          setIsTotalSessionsLoading(false);
          setTotalSessions(data.length);
        },
        onError: () => {
          setIsTotalSessionsLoading(false);
          setTotalSessions(0);
        },
      })
    );

    const trainingTimes = [
      {
        from_date: dayjs().startOf("week").toDate(),
        to_date: dayjs().endOf("day").toDate(),
        period_type: PeriodType.WEEKLY,
        setLoading: setIsWeekTimeLoading,
        setTrainingTime: setWeeklyTrainingTime,
      },
      {
        from_date: dayjs().startOf("month").toDate(),
        to_date: dayjs().endOf("day").toDate(),
        period_type: PeriodType.MONTHLY,
        setLoading: setIsMonthTimeLoading,
        setTrainingTime: setMonthlyTrainingTime,
      },
      {
        period_type: PeriodType.ALL_TIME,
        setLoading: setIsTotalTimeLoading,
        setTrainingTime: setTotalTrainingTime,
      },
    ];

    trainingTimes.forEach(time => {
      apiPromises.push(
        dispatch({
          type: Actions.StatsActions.GetUserStats,
          payload: {
            user_ids: [state.user.id],
            from_date: time.from_date,
            to_date: time.to_date,
            period_type: time.period_type,
            game_mode: GameModes.SCENARIO,
            is_completed: true,
            stats: [
              {
                score_name: ScoreFieldTypes.GAMEPLAY_DURATION,
                score_field: ScoreFieldTypes.GAMEPLAY_DURATION,
                score_type: ScoreTypes.TOTAL,
              },
            ],
          },
          disableLoaders: true,
          cacheTime: [5, "minutes"],
          onSuccess: stats => {
            time.setLoading(false);
            if (stats.length > 0) {
              const gameplayDuration = stats.reduce((acc: number, stat: UserStats) => acc + (stat?.gameplay_duration || 0), 0);
              time.setTrainingTime(gameplayDuration);
            };
          },
        })
      );
    });

    apiPromises.push(
      dispatch({
        type: Actions.StatsActions.GetUserSessions,
        payload: {
          user_id: state.user.id,
          is_completed: true,
          game_mode: GameModes.SCENARIO,
          from_date: getFromDate(DateRange.ALL_TIME),
          to_date: dayjs().toDate(),
        },
        disableLoaders: true,
        cacheTime: [5, "minutes"],
        onError: (error: any) => {
          setIsWeeklyDailyStatsLoading(false);
          setIsMonthlyDailyStatsLoading(false);
          setIsStreakLoading(false);

          setWeeklyDailyStats([]);
          setMonthlyDailyStats([]);
          setTrainingStreakWeeks(0);

          console.error("Failed to fetch user sessions:", error);
        },
        onSuccess: sessions => {
          const weekStart = dayjs().startOf("week");
          const weekEnd = dayjs().endOf("day");
          const weeklySessions = sessions.filter((session: any) => {
            if (!session.updated_at) return false;
            const sessionDate = dayjs(session.updated_at);
            return (
              (sessionDate.isAfter(weekStart) || sessionDate.isSame(weekStart)) && (sessionDate.isBefore(weekEnd) || sessionDate.isSame(weekEnd))
            );
          });

          const monthStart = dayjs().startOf("month");
          const monthEnd = dayjs().endOf("day");
          const monthlySessions = sessions.filter((session: any) => {
            if (!session.updated_at) return false;
            const sessionDate = dayjs(session.updated_at);
            return (
              (sessionDate.isAfter(monthStart) || sessionDate.isSame(monthStart)) && (sessionDate.isBefore(monthEnd) || sessionDate.isSame(monthEnd))
            );
          });

          setIsWeeklyDailyStatsLoading(false);
          setWeeklyDailyStats(weeklySessions);

          setIsMonthlyDailyStatsLoading(false);
          setMonthlyDailyStats(monthlySessions);

          setIsStreakLoading(false);
          const streak = calculateTrainingStreak(sessions);
          setTrainingStreakWeeks(streak);

          setCurrentBybIq(calculateHeadlineBybIq(sessions));
          setIsCurrentBybIqLoading(false);
        },
      })
    );

    Promise.all(apiPromises)

  }, []);

  useEffect(() => {
    if (!currentStats) {
      setRadarData([]);

      return;
    }

    setRadarData(parseRadarStats(currentStats, previousStats));
  }, [currentStats, previousStats]);

  return {
    totalSessions,
    requiredTrainingTime,
    weeklyTrainingTime,
    totalTrainingTime,
    monthlyTrainingTime,
    weeklyDailyStats,
    monthlyDailyStats,
    currentStats,
    previousStats,
    radarData,
    historicalResults,
    longTermResults,
    longTermMetricResults,
    currentBybIq,
    improvementPercentage,
    isCurrentBybIqLoading,
    trainingStreakWeeks,
    isRecentRadarLoading,
    isPreviousRadarLoading,
    isWeekTimeLoading,
    isMonthTimeLoading,
    isTotalTimeLoading,
    isTotalSessionsLoading,
    isLongTermResultsLoading,
    setIsLongTermResultsLoading,
    isWeeklyDailyStatsLoading,
    isMonthlyDailyStatsLoading,
    isStreakLoading,
    dateRange,
    optionsAnchorElement,
    setDateRange,
    setOptionsAnchorElement,
    isInBootcamp,
    isDemo,
    navigateToChallenges,
  };
};
